if (!customElements.get("block-cross-sell")) {
  class BlockCrossSell extends Element {
    props = {
      mainProduct: {},
      mainProductVariant: {},
      mainProductVariants: [],
      crossSellProduct: {},
      crossSellProductVariants: [],
      description: "",
      showDrawer: true,
      quantityLabel: "Qty",
      crossSellTotal: 0,
      crossSellIndex: 0,
      quantity: 1,
      status: "idle",
      currentMainProductVariant: {},
      currentCrossSellProductVariant: {},
    };

    setup(props) {
      this.data.currentMainProductVariant = props.mainProductVariant;
      this.data.currentCrossSellProductVariant = this.getMatchedVariantSize(props.mainProductVariant);
    }

    setupListeners() {
      ProductPageStore.subscribe((event) => {
        this.handleMainProductVariantChange(event.detail.currentVariant);
      });

      this.addEventListener("cross-sell:add", () => {
        this.data.status = "active";
        this.notifyStateChange();
      });

      this.addEventListener("cross-sell:remove", () => {
        this.data.status = "idle";
        this.data.quantity = 1;
        this.notifyStateChange();
      });

      this.addEventListener("cross-sell:quantity-change", (event) => {
        this.data.quantity = event.detail.quantity;
        this.notifyStateChange();
      });

      this.addEventListener("cross-sell:drawer-open", () => {
        this.handleCrossSellDrawerOpen();
      });
    }

    getMatchedVariantSize(mainProductVariant) {
      if (!this.props.crossSellProductVariants) return undefined;
      const size = mainProductVariant?.title?.split("(")?.[0]?.trim()?.toLowerCase();
      if (!size) return undefined;
      const regex = new RegExp(String.raw`^${size}$`, "i");
      const matchedVariant = this.props.crossSellProductVariants.find((variant) =>
        regex.test(variant.title.split("(")?.[0]?.trim()?.toLowerCase()),
      );
      return matchedVariant || this.props.crossSellProductVariants[0];
    }

    handleMainProductVariantChange(mainProductVariant) {
      const variant = this.getMatchedVariantSize(mainProductVariant);
      this.data.currentMainProductVariant = mainProductVariant;
      this.data.currentCrossSellProductVariant = variant;
      if (!variant?.available) this.data.status = "idle";
    }

    notifyStateChange() {
      this.dispatchEvent(
        new CustomEvent("update", {
          bubbles: true,
          detail: { ...this.data, crossSellProductId: this.props.crossSellProduct.id },
        }),
      );
    }

    getAddToCartData() {
      return {
        isAdded: this.data.status === "active",
        quantity: Number(this.data.quantity),
        variantId: this.data.currentMainProductVariant?.id,
        crossSellVariantId: this.data.currentCrossSellProductVariant?.id,
        status: this.data.status,
        mainProduct: this.props.mainProduct,
      };
    }

    handleCrossSellDrawerOpen() {
      window.eventBus.publish("cross-sell-drawer:open", {
        title: this.props.crossSellProduct.title,
        drawerDescription: this.shadowRoot?.querySelector("cross-sell-drawer-description")?.innerHTML,
        image: this.props.crossSellProduct.featured_image,
        link: "/products/" + this.props.crossSellProduct.handle,
        primaryButton: {
          text: "製品を見る",
          onClick: (state, closeDrawer) => {
            window.open(state.link, "_blank");
            closeDrawer();
          },
        },
        secondaryButton: {
          text: "買い物を続ける",
          onClick: (state, closeDrawer) => closeDrawer(),
        },
      });
    }

    get shouldShowItem() {
      const available = this.data.currentCrossSellProductVariant?.available;
      if (this.props.crossSellTotal <= 1) return true;
      if (available) return true;
      return this.props.crossSellIndex >= this.props.crossSellTotal;
    }

    get variantLabel() {
      const title = this.data.currentCrossSellProductVariant?.title;
      if (!title || title === "Default Title") return "";
      return title + " | ";
    }

    template() {
      if (!this.shouldShowItem) return `<div class="hidden"></div>`;

      const variant = this.data.currentCrossSellProductVariant;
      const available = !!variant?.available;
      const price = currencyFormatter.format(((variant?.price || 0) / 100) * this.data.quantity);
      const compareAtPrice = currencyFormatter.format(((variant?.compare_at_price || 0) / 100) * this.data.quantity);
      const isDiscounted = variant?.compare_at_price > variant?.price;

      return `
        <div class="flex items-center gap-4">
          <img src="${this.props.crossSellProduct.featured_image}&width=100" width="50" height="50" class="rounded size-[50px] object-cover" alt="${this.props.crossSellProduct.title}">
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-[16px] text-[var(--color-neutral-900)]">${this.props.crossSellProduct.title}</p>
            <p class="text-[14px] text-[var(--color-neutral-800)]">${this.variantLabel}${this.props.quantityLabel}: ${this.data.quantity}</p>
            <div class="flex items-center gap-1">
              <span class="text-[14px] text-[var(--color-neutral-900)]">${price}</span>
              ${isDiscounted ? `<span class="text-[14px] text-[var(--color-neutral-800)] line-through">${compareAtPrice}</span>` : ""}
            </div>
          </div>
          <cross-sell-item-add-button :available="${available}" :status="${this.data.status}"></cross-sell-item-add-button>
        </div>
        <div class="flex flex-col gap-2 mt-4">
          <cross-sell-item-quantity class="${this.data.status === "idle" ? "hidden" : ""}" :quantity="${this.data.quantity}" :label="${this.props.quantityLabel}"></cross-sell-item-quantity>
          <p class="text-[14px] text-[var(--color-neutral-800)]">${this.props.description}</p>
          ${
            this.props.showDrawer
              ? `<button type="button" data-action="drawer-open" class="flex items-center gap-2 text-[14px] font-bold underline w-max">
            商品の詳細を読む
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.36 7.52661L6.5867 3.75994C6.52472 3.69746 6.45099 3.64786 6.36975 3.61402C6.28851 3.58017 6.20137 3.56274 6.11336 3.56274C6.02535 3.56274 5.93822 3.58017 5.85698 3.61402C5.77574 3.64786 5.702 3.69746 5.64003 3.75994C5.51586 3.88485 5.44617 4.05382 5.44617 4.22994C5.44617 4.40607 5.51586 4.57503 5.64003 4.69994L8.94003 8.03328L5.64003 11.3333C5.51586 11.4582 5.44617 11.6272 5.44617 11.8033C5.44617 11.9794 5.51586 12.1484 5.64003 12.2733C5.70177 12.3363 5.7754 12.3864 5.85665 12.4207C5.9379 12.455 6.02516 12.4729 6.11336 12.4733C6.20157 12.4729 6.28882 12.455 6.37007 12.4207C6.45132 12.3864 6.52495 12.3363 6.5867 12.2733L10.36 8.50661C10.4277 8.44418 10.4817 8.36841 10.5186 8.28408C10.5556 8.19975 10.5747 8.10868 10.5747 8.01661C10.5747 7.92454 10.5556 7.83347 10.5186 7.74914C10.4817 7.66481 10.4277 7.58904 10.36 7.52661Z" fill="var(--color-neutral-900)"></path>
            </svg>
          </button>`
              : ""
          }
        </div>
      `;
    }

    onUpdated() {
      this.querySelector('[data-action="drawer-open"]')?.addEventListener("click", () => {
        this.dispatchEvent(new CustomEvent("cross-sell:drawer-open", { bubbles: true }));
      });
    }
  }
  customElements.define("block-cross-sell", BlockCrossSell);
}

if (!customElements.get("cross-sell-item-add-button")) {
  class CrossSellItemAddButton extends Element {
    props = { available: true, status: "idle" };

    template() {
      const isActive = this.props.status === "active";
      return `
        <button type="button" data-role="add" ${this.props.available ? "" : "disabled"} class="${isActive ? "hidden" : ""} bg-[var(--color-primary-500)] disabled:opacity-50 text-[var(--color-neutral-900)] font-semibold text-[14px] rounded-full flex items-center justify-center gap-1 min-h-9 max-h-9 px-4 whitespace-nowrap">
          ${this.props.available ? "追加する" : "在庫切れ"}
        </button>
        <button type="button" data-role="remove" aria-label="Remove" class="${isActive ? "" : "hidden"} bg-[var(--color-neutral-50)] rounded-full flex items-start min-w-9 max-w-9 min-h-9 max-h-9 p-2">
          <svg width="20" height="20" viewBox="0 0 24 24" class="mx-auto" fill="var(--color-neutral-900)" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.06 12l6.47-6.47a.75.75 0 10-1.06-1.06L12 10.94 5.53 4.47a.75.75 0 10-1.06 1.06L10.94 12l-6.47 6.47a.75.75 0 101.06 1.06L12 13.06l6.47 6.47a.747.747 0 001.06 0 .75.75 0 000-1.06L13.06 12z"></path>
          </svg>
        </button>
      `;
    }

    onUpdated() {
      this.querySelector('[data-role="add"]').onclick = () =>
        this.dispatchEvent(new CustomEvent("cross-sell:add", { bubbles: true }));
      this.querySelector('[data-role="remove"]').onclick = () =>
        this.dispatchEvent(new CustomEvent("cross-sell:remove", { bubbles: true }));
    }
  }
  customElements.define("cross-sell-item-add-button", CrossSellItemAddButton);
}

if (!customElements.get("cross-sell-item-quantity")) {
  class CrossSellItemQuantity extends Element {
    props = { quantity: 1, label: "Quantity" };

    template() {
      const isDecrementDisabled = this.props.quantity <= 1;
      return `
        <div>
          <p>${this.props.label}</p>
          <div class="quantity-adjuster">
            <button type="button" data-role="decrement" ${isDecrementDisabled ? "disabled" : ""} class="quantity-adjuster-button">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path transform="translate(3.3333 9.375)" d="M12.8205 7.93123e-06C8.71795 7.93123e-06 4.61538 -9.91404e-06 0.51282 7.93123e-06C0.229703 7.93123e-06 0 0.279957 0 0.625007C0 0.970057 0.229703 1.25001 0.51282 1.25001C4.61538 1.25001 8.71795 1.25001 12.8205 1.25001C13.1036 1.25001 13.3333 0.970057 13.3333 0.625007C13.3333 0.279957 13.1036 7.93123e-06 12.8205 7.93123e-06Z" fill="currentColor"/>
              </svg>
            </button>
            <input value="${this.props.quantity}" class="quantity-adjuster__value" type="text" inputmode="numeric" size="${String(this.props.quantity).length}" readonly>
            <button type="button" data-role="increment" class="quantity-adjuster-button">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path transform="translate(1.875 1.875)" d="M15.625 7.5H8.75V0.625C8.75 0.27995 8.47005 0 8.125 0C7.77995 0 7.5 0.27995 7.5 0.625V7.5H0.625C0.27995 7.5 0 7.77995 0 8.125C0 8.47005 0.27995 8.75 0.625 8.75H7.5V15.625C7.5 15.97 7.77995 16.25 8.125 16.25C8.47005 16.25 8.75 15.97 8.75 15.625V8.75H15.625C15.97 8.75 16.25 8.47005 16.25 8.125C16.25 7.77995 15.97 7.5 15.625 7.5Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    }

    onUpdated() {
      this.querySelector('[data-role="increment"]').onclick = () =>
        this.dispatchEvent(
          new CustomEvent("cross-sell:quantity-change", { bubbles: true, detail: { quantity: this.props.quantity + 1 } }),
        );
      this.querySelector('[data-role="decrement"]').onclick = () =>
        this.dispatchEvent(
          new CustomEvent("cross-sell:quantity-change", {
            bubbles: true,
            detail: { quantity: Math.max(1, this.props.quantity - 1) },
          }),
        );
    }
  }
  customElements.define("cross-sell-item-quantity", CrossSellItemQuantity);
}
